
import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';

const HeroSection = () => {
  const [loadingProgress, setLoadingProgress] = useState(10);
  const [heroText, setHeroText] = useState('');
  const navigate = useNavigate();
  const fullText = "Transform Through Cosmic Horror";
  
  useEffect(() => {
    const interval = setInterval(() => {
      setLoadingProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          return 100;
        }
        return prev + 10;
      });
    }, 500);
    
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    let currentIndex = 0;
    const typeText = () => {
      if (currentIndex <= fullText.length) {
        setHeroText(fullText.slice(0, currentIndex));
        currentIndex++;
        setTimeout(typeText, 100);
      }
    };
    
    const delay = setTimeout(() => {
      typeText();
    }, 2000);
    
    return () => clearTimeout(delay);
  }, []);
  
  const getProgressBar = () => {
    const filled = Math.floor(loadingProgress / 10);
    return `[${filled > 0 ? '█'.repeat(filled) : ''}${'░'.repeat(10 - filled)}] ${loadingProgress}%`;
  };
  
  return (
    <section id="hero" className="relative min-h-screen w-full pt-16 flex flex-col justify-center items-center overflow-hidden">
      {/* Video Background */}
      <div className="absolute inset-0 overflow-hidden z-0">
        <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
        >
          <source src="/videos/header-bg.mp4" type="video/mp4" />
        </video>
        {/* Overlay gradients for readability */}
        <div className="absolute inset-0 bg-black/50 z-10"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-transparent to-black z-10"></div>
        <div className="absolute inset-0 bg-noise opacity-10 z-20"></div>
      </div>
      
      {/* Content */}
      <div className="relative z-30 container mx-auto px-4 md:px-8 text-center">
        <div className="analog-text font-mono text-xs md:text-sm mb-6 opacity-70">
          <p>Neural Interface Synchronization {getProgressBar()}</p>
        </div>
        
        <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6 glitch-title text-green-400 tracking-tight min-h-[1.2em]">
          <span className="typing-cursor">
            {heroText}
            {heroText.length < fullText.length && <span className="animate-pulse">|</span>}
          </span>
        </h1>
        
        <div className="max-w-3xl mx-auto space-y-6 mb-10">
          <p className="text-lg md:text-xl text-gray-300 mb-4 analog-text leading-relaxed">
            Weirdobeach channels forgotten frequencies to reshape physical reality. 
            Interface with entities beyond comprehension, track transformation through mystical protocols, 
            and witness your corporeal form bend to cosmic will.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div className="border border-gray-700 bg-zinc-900/30 p-3">
              <span className="text-green-400 font-bold">⬡ Neural Tracking</span>
              <p className="text-gray-400 mt-1">Dimensional measurement protocols</p>
            </div>
            <div className="border border-gray-700 bg-zinc-900/30 p-3">
              <span className="text-green-400 font-bold">⬢ Entity Communion</span>
              <p className="text-gray-400 mt-1">Direct otherworldly guidance</p>
            </div>
            <div className="border border-gray-700 bg-zinc-900/30 p-3">
              <span className="text-green-400 font-bold">⬣ Void Progression</span>
              <p className="text-gray-400 mt-1">Ascension through frequencies</p>
            </div>
          </div>
        </div>
        
        <div className="flex flex-col sm:flex-row justify-center items-center gap-4 mb-12">
          <Button 
            variant="outline" 
            className="pulse-button bg-green-500 border-green-500 text-black py-6 px-8 text-lg font-bold hover:bg-green-600 hover:border-green-600 transform transition-all duration-300 hover:scale-105"
            onClick={() => navigate('/auth')}
          >
            Begin Neural Interface
          </Button>
          
          <Button 
            variant="outline" 
            className="bg-transparent border border-gray-600 text-gray-400 py-6 px-8 text-lg hover:border-green-500 hover:text-green-500"
            onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })}
          >
            Explore Frequencies
          </Button>
        </div>
        
        <div className="mt-12 md:mt-16 text-xs analog-text opacity-50">
          <p className="font-mono">* Signal strength varies by dimensional alignment coefficient</p>
        </div>
      </div>
      
      {/* Enhanced scanlines */}
      <div className="absolute inset-0 vhs-tracking-lines pointer-events-none z-40 opacity-30"></div>
    </section>
  );
};

export default HeroSection;
